import { memo, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { FioFields } from '@entities/fio-fields';

import {
  AUTH,
  DatepickerController,
  FormControlContainer,
  InputController,
  InternalLink,
  RGS_AGENT_REGISTRATON,
  getDateForAge,
} from '@shared/index';

import type { RegistrationFormProps } from '../model/types/registration-form.types';
import { ValidationError } from './registration.styles';
import { RGSAgentRegistrationButton } from './rgs-agent-registration-button';

const maxBirthDate = getDateForAge(18);
maxBirthDate.setDate(maxBirthDate.getDate() - 1);
const minBirthDate = getDateForAge(100);

export const Registration = memo(() => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<RegistrationFormProps>();
  const { root } = errors || {};
  const { phoneAlreadyExist } = root || {};

  const handleClearErrors = useCallback(() => {
    if (errors) {
      clearErrors();
    }
  }, [errors]);

  return (
    <>
      <RGSAgentRegistrationButton />
      <FioFields testId='registration' />
      <FormControlContainer $margin='0 0 16px 0'>
        <DatepickerController<RegistrationFormProps>
          control={control}
          name='birthDate'
          label={t('REGISTRATION:labels.birthDate') || ''}
          id='birthDate'
          onChange={handleClearErrors}
          minDate={minBirthDate}
          maxDate={maxBirthDate}
          showMonthDropdown
          showYearDropdown
          showInput
          testId='registration.birthDate'
        />
      </FormControlContainer>
      <FormControlContainer $margin='0 0 16px 0'>
        <InputController<RegistrationFormProps>
          control={control}
          name='phone'
          label={t('COMMON:labels.phone') || ''}
          id='phone'
          onChange={handleClearErrors}
          type='tel'
          testId='registration.phone'
          hint={
            <>
              {phoneAlreadyExist?.message && (
                <ValidationError>
                  <Trans components={{ a: <InternalLink to={AUTH} /> }}>
                    {t('COMMON:errors.phoneAlreadyExistAuth')}
                  </Trans>
                  <Trans
                    components={{
                      a: <InternalLink to={RGS_AGENT_REGISTRATON} />,
                    }}
                  >
                    {t('COMMON:errors.phoneAlreadyExistAgent')}
                  </Trans>
                </ValidationError>
              )}
            </>
          }
        />
      </FormControlContainer>
      <FormControlContainer $margin='0 0 16px 0'>
        <InputController<RegistrationFormProps>
          control={control}
          name='email'
          label={t('REGISTRATION:labels.email') || ''}
          id='email'
          onChange={handleClearErrors}
          type='text'
          testId='registration.email'
        />
      </FormControlContainer>
    </>
  );
});
